import React, { useEffect, useState, cloneElement, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ChevronDown, Calendar, MessageCircle, ArrowLeft, Phone, Clock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SERVICES_DATA } from '../data/servicesData';
import { twMerge } from 'tailwind-merge';
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const service = SERVICES_DATA.find(s => s.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!service) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-784">
        <h1 className="text-3xl font-heading font-bold text-brand-navy mb-4" data-id="element-785">
          Service Not Found
        </h1>
        <p className="text-slate-600 mb-8" data-id="element-786">
          The service you are looking for does not exist or has been moved.
        </p>
        <Button href="/services" variant="primary" data-id="element-787">
          View All Services
        </Button>
      </div>;
  }
  const whatsappMessage = encodeURIComponent(`Hello Maisha Medical Center.\nI would like to inquire about ${service.title}.\nPlease assist me.`);
  return <div className="min-h-screen bg-brand-light pb-20" data-id="element-788">
      {/* Hero Band */}
      <div className="bg-brand-navy py-16 relative overflow-hidden" data-id="element-789">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-790">
          <button onClick={() => navigate('/services')} className="flex items-center gap-2 text-brand-gold hover:text-white transition-colors text-sm font-medium mb-8" data-id="element-791">
            <ArrowLeft className="w-4 h-4" data-id="element-792" /> Back to Services
          </button>
          <div className="flex items-center gap-4 mb-6" data-id="element-793">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-brand-gold backdrop-blur-sm" data-id="element-794">
              {cloneElement(service.icon as React.ReactElement, {
              className: 'w-8 h-8'
            })}
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white" data-id="element-795">
              {service.title}
            </h1>
          </div>
          <p className="text-lg text-slate-300 max-w-3xl leading-relaxed" data-id="element-796">
            {service.shortDesc}
          </p>
        </div>
      </div>

      <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-797">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-798">
          {/* Main Content */}
          <div className="w-full lg:w-2/3 space-y-12" data-id="element-799">
            {/* Overview */}
            <section data-id="element-800">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-4" data-id="element-801">
                Overview
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed" data-id="element-802">
                {service.overview}
              </p>
            </section>

            {/* Benefits */}
            <section className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-803">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-804">
                Key Benefits
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-805">
                {service.benefits.map((benefit, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-806">
                    <CheckCircle2 className="w-6 h-6 text-brand-gold shrink-0 mt-0.5" data-id="element-807" />
                    <span className="text-slate-700 font-medium" data-id="element-808">
                      {benefit}
                    </span>
                  </div>)}
              </div>
            </section>

            {/* Process */}
            <section data-id="element-809">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-8" data-id="element-810">
                What to Expect
              </h2>
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-811">
                {service.process.map((step, idx) => <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-812">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-brand-light bg-brand-navy text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10" data-id="element-813">
                      {idx + 1}
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-2xl shadow-sm border border-slate-100" data-id="element-814">
                      <h3 className="font-heading font-bold text-brand-navy text-lg mb-2" data-id="element-815">
                        {step.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed" data-id="element-816">
                        {step.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </section>

            {/* FAQs */}
            <section data-id="element-817">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-818">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4" data-id="element-819">
                {service.faqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return <div key={idx} className={twMerge('bg-white border rounded-2xl overflow-hidden transition-all duration-300', isOpen ? 'border-brand-navy shadow-soft' : 'border-slate-200 hover:border-brand-navy/30')} data-id="element-820">
                      <button onClick={() => setOpenFaqIndex(isOpen ? null : idx)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-821">
                        <span className="font-heading font-semibold text-lg text-brand-navy pr-8" data-id="element-822">
                          {faq.q}
                        </span>
                        <ChevronDown className={twMerge('w-5 h-5 text-brand-red shrink-0 transition-transform duration-300', isOpen ? 'rotate-180' : '')} data-id="element-823" />
                      </button>
                      <div className={twMerge('px-6 overflow-hidden transition-all duration-300 ease-in-out', isOpen ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0')} data-id="element-824">
                        <p className="text-slate-600 leading-relaxed" data-id="element-825">
                          {faq.a}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-1/3 space-y-6" data-id="element-826">
            {/* Action Card */}
            <div className="bg-white p-8 rounded-2xl shadow-soft border border-slate-100 sticky top-28" data-id="element-827">
              <h3 className="font-heading font-bold text-xl text-brand-navy mb-2" data-id="element-828">
                Ready to get started?
              </h3>
              <p className="text-slate-600 text-sm mb-8" data-id="element-829">
                Book your {service.title.toLowerCase()} appointment today.
              </p>

              <div className="space-y-4" data-id="element-830">
                <Button href={`/book?service=${service.slug}`} variant="primary" fullWidth icon={<Calendar className="w-5 h-5" data-id="element-832" />} data-id="element-831">
                  Book Appointment
                </Button>
                <Button href={`https://wa.me/26773241613?text=${whatsappMessage}`} target="_blank" variant="outline" fullWidth icon={<MessageCircle className="w-5 h-5 text-[#25D366]" data-id="element-834" />} className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-brand-navy" data-id="element-833">
                  Inquire via WhatsApp
                </Button>
              </div>

              <hr className="my-8 border-slate-100" data-id="element-835" />

              <div className="space-y-4" data-id="element-836">
                <div className="flex items-start gap-3" data-id="element-837">
                  <Phone className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-838" />
                  <div data-id="element-839">
                    <div className="text-sm font-medium text-brand-navy" data-id="element-840">
                      Call Us Directly
                    </div>
                    <a href="tel:+2676540244" className="text-brand-red font-semibold hover:underline" data-id="element-841">
                      +267 6540244
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-842">
                  <Clock className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-843" />
                  <div data-id="element-844">
                    <div className="text-sm font-medium text-brand-navy" data-id="element-845">
                      Operating Hours
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-846">
                      Mon - Fri: 8:00 AM - 6:00 PM
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-847">
                      Sat: 9:00 AM - 1:00 PM
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}